import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Fade, Slide } from 'react-awesome-reveal';
import { PageContainer } from '@/components/layout';
import { Card, CardHeader, CardTitle, Badge } from '@/components/common';
import { useAuth } from '@/context';
import { useMyDayDashboard } from '@/api/dashboard';

// ── Styled components (kept from the pre-11E.8 page for visual continuity) ──

const WelcomeBanner = styled.div`
  background: ${({ theme }) => theme.colors.cardBgGradient};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #10B981 0%, #34D399 50%, #6EE7B7 100%);
  }
`;

const WelcomeTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const WelcomeSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  margin: 0;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: 6px 12px;
  background: linear-gradient(135deg, #10B981 0%, #34D399 100%);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: #fff;
  margin-left: ${({ theme }) => theme.spacing.md};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
  }
`;

const BalanceLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const BalanceValue = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const ApplyLeaveButton = styled(Link)`
  display: inline-block;
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  text-decoration: none;
  transition: transform 0.15s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const ConcernsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ConcernRow = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-decoration: none;
  color: inherit;
  transition: transform 0.15s;

  &:hover {
    transform: translateX(4px);
  }
`;

const ConcernName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const ConcernMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const EmptyNote = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  margin: 0;
`;

function concernReasonLabel(reason: 'consecutive' | 'percentage' | 'both', consecutive: number, pct: number | null): string {
  if (reason === 'both') return `${consecutive} missed in a row · ${pct ?? '—'}% present`;
  if (reason === 'consecutive') return `${consecutive} missed in a row`;
  return `${pct ?? '—'}% present (trailing 30 days)`;
}

const TeacherDashboard: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading } = useMyDayDashboard();

  return (
    <PageContainer
      title="My Day"
      subtitle="Your leave balance and student attendance concerns"
      data-testid="candeza-dashboard-page"
    >
      <Fade triggerOnce>
        <WelcomeBanner>
          <WelcomeTitle>
            Good day, {user?.email?.split('@')[0]}!
            <RoleBadge>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c3 3 9 3 12 0v-5" />
              </svg>
              Teacher
            </RoleBadge>
          </WelcomeTitle>
          <WelcomeSubtitle>
            Your calendar and register live under Calendar — this page is your leave balance and
            student attendance concerns at a glance.
          </WelcomeSubtitle>
        </WelcomeBanner>
      </Fade>

      <ContentGrid>
        <Slide triggerOnce direction="left">
          <Card padding="lg" data-testid="candeza-dash-leave-balance">
            <CardHeader>
              <CardTitle>My Leave Balance</CardTitle>
            </CardHeader>
            {isLoading && <EmptyNote>Loading…</EmptyNote>}
            {!isLoading && data && (
              <>
                <BalanceRow>
                  <BalanceLabel>Allocated</BalanceLabel>
                  <BalanceValue>{data.leave_balance.allocated}</BalanceValue>
                </BalanceRow>
                <BalanceRow>
                  <BalanceLabel>Availed</BalanceLabel>
                  <BalanceValue>{data.leave_balance.availed}</BalanceValue>
                </BalanceRow>
                <BalanceRow>
                  <BalanceLabel>Balance</BalanceLabel>
                  <BalanceValue>{data.leave_balance.balance}</BalanceValue>
                </BalanceRow>
                <ApplyLeaveButton to="/my-leaves" data-testid="candeza-dash-action-apply-leave">
                  Apply Leave
                </ApplyLeaveButton>
              </>
            )}
          </Card>
        </Slide>

        <Slide triggerOnce direction="right">
          <Card padding="lg" data-testid="candeza-dash-attendance-concerns">
            <CardHeader>
              <CardTitle>My Students' Attendance Concerns</CardTitle>
              {!isLoading && data && (
                <Badge variant={data.attendance_concerns.length > 0 ? 'warning' : 'success'}>
                  {data.attendance_concerns.length}
                </Badge>
              )}
            </CardHeader>
            {isLoading && <EmptyNote>Loading…</EmptyNote>}
            {!isLoading && data?.attendance_concerns.length === 0 && (
              <EmptyNote>No attendance concerns right now.</EmptyNote>
            )}
            {!isLoading && data && data.attendance_concerns.length > 0 && (
              <ConcernsList>
                {data.attendance_concerns.map((c) => (
                  <ConcernRow key={c.student_id} to={`/students/${c.student_id}`}>
                    <div>
                      <ConcernName>{c.student_name}</ConcernName>
                      <ConcernMeta>{concernReasonLabel(c.reason, c.consecutive_missed, c.pct_present)}</ConcernMeta>
                    </div>
                  </ConcernRow>
                ))}
              </ConcernsList>
            )}
          </Card>
        </Slide>
      </ContentGrid>
    </PageContainer>
  );
};

export default TeacherDashboard;
